// client/src/components/pages/Geofencing/LiveMapLegend.tsx
//
// Live View's map key (ST-00244 / ST-00253): what the marker colours and shapes mean. Colour is
// the shared asset status scheme (map/assetStatus) — the same as on the Status Board and Replay
// — listed for the keys on the map right now; shape says moving (pointed), standing (round) or
// no recent position (dashed); a dark numbered circle is several vehicles close together.
// A small toggle beside the basemap switch; the panel opens above it. The colour entries are
// generated from the active rules (map/assetVisualRules legendFromRules).
//
// ST-00276 phase 2 (map colours & states): with the organisation's rule set (`legend`, the status
// board's display_rules.legend) the key also lists the organisation's own colour rules ("Low fuel")
// and the marks (badge / ring / pulse) with the condition behind each, in words.

import { useId, useState } from 'react';
import { AssetMarkerIcon } from '../../map/assetMarker';
import { ASSET_STATUS, type AssetStatusKey } from '../../map/assetStatus';
import { legendFromRules } from '../../map/assetVisualRules';
import { MarkGlyph } from '../../map/DisplayMarks';
import type { LegendEntry } from '../../map/displayRules';

export interface LiveMapLegendProps {
  /** The status keys of the vehicles on the map. */
  keys: readonly AssetStatusKey[];
  /** ST-00276: the organisation's display rules' legend (null: the default rules alone). */
  legend?: readonly LegendEntry[] | null;
}

export default function LiveMapLegend({ keys, legend = null }: LiveMapLegendProps) {
  const ownRules = (legend || []).filter((e) => e.kind === 'rule' && e.token);
  // The dashed outline has its own line under Shape.
  const marks = (legend || []).filter((e) => e.kind === 'overlay' && e.mark && e.mark !== 'dashed');
  const [open, setOpen] = useState(false);
  const panelId = useId();
  // The colours: generated from the active rules, for the keys on the map (offline is the
  // dashed shape below).
  const present = legendFromRules({}, { keys: keys.filter((k) => k !== 'offline'), overlayIds: [] })
    .map((e) => e.key)
    .filter((k): k is AssetStatusKey => !!k);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className={`px-3 py-1.5 rounded-lg shadow-lg text-xs font-medium transition-colors ${
          open
            ? 'bg-teal-500 text-white'
            : 'bg-surface/95 backdrop-blur text-gray-600 hover:bg-gray-100'
        }`}
      >
        Map key
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label="Map key"
          className="absolute bottom-full left-0 mb-2 w-64 max-h-[60vh] overflow-y-auto bg-surface/95 backdrop-blur rounded-lg shadow-lg p-3 text-xs text-slate-700"
        >
          <p className="font-semibold text-slate-800 mb-1.5">Colour: what it is doing</p>
          {present.length === 0 ? (
            <p className="text-slate-500 mb-2">No vehicles on the map.</p>
          ) : (
            <ul className="space-y-1 mb-3">
              {present.map((k) => (
                <li key={k} className="flex items-center gap-2" data-legend-key={k}>
                  <AssetMarkerIcon status={k} size={22} className="flex-shrink-0" />
                  <span>
                    <span className="font-medium text-slate-800">{ASSET_STATUS[k].label}</span>
                    <span className="text-slate-500"> · {ASSET_STATUS[k].hint.toLowerCase()}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {ownRules.length > 0 && (
            <>
              <p className="font-semibold text-slate-800 mb-1.5">Your organisation’s colours</p>
              <ul className="space-y-1 mb-3">
                {ownRules.map((e) => (
                  <li key={e.id} className="flex items-center gap-2" data-legend-rule={e.id}>
                    <AssetMarkerIcon status={e.token as AssetStatusKey} size={22} className="flex-shrink-0" />
                    <span>
                      <span className="font-medium text-slate-800">{e.label}</span>
                      {e.hint && <span className="text-slate-500"> · {e.hint}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {marks.length > 0 && (
            <>
              <p className="font-semibold text-slate-800 mb-1.5">Marks</p>
              <ul className="space-y-1 mb-3">
                {marks.map((e) => (
                  <li key={e.id} className="flex items-center gap-2" data-legend-mark={e.id}>
                    <span className="inline-flex w-[22px] justify-center flex-shrink-0">
                      <MarkGlyph mark={e.mark!} token={e.token} />
                    </span>
                    <span>
                      <span className="font-medium text-slate-800">{e.label}</span>
                      {e.hint && <span className="text-slate-500"> · {e.hint}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="font-semibold text-slate-800 mb-1.5">Shape</p>
          <ul className="space-y-1">
            <li className="flex items-center gap-2">
              <AssetMarkerIcon
                status="parked"
                heading={60}
                moving
                size={22}
                className="flex-shrink-0"
              />
              Moving: the point shows the way it drives
            </li>
            <li className="flex items-center gap-2">
              <AssetMarkerIcon status="parked" size={22} className="flex-shrink-0" />
              Standing still
            </li>
            <li className="flex items-center gap-2">
              <AssetMarkerIcon status="offline" size={22} className="flex-shrink-0" />
              Dashed: no position for 5 minutes or more
            </li>
            {marks.length > 0 && (
              <li className="flex items-center gap-2" data-legend-more="">
                <span
                  className="inline-flex items-center justify-center flex-shrink-0 w-[22px] h-[16px] rounded-full bg-slate-700 text-white text-[10px] font-bold ring-2 ring-white"
                  aria-hidden="true"
                >
                  F+
                </span>
                A dot beside a truck: its badge’s first letter; + means more marks — open the
                truck to see them all
              </li>
            )}
            <li className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center flex-shrink-0 w-[22px] h-[22px] rounded-full bg-[#1e293b] text-white text-[10px] font-bold ring-2 ring-white shadow"
                aria-hidden="true"
              >
                4
              </span>
              Vehicles close together: click to zoom in
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
